import { Form, Input, InputNumber, Modal, Select, message } from "antd";
import React, { useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { updateGroupStock } from "../../services/groupConsole.service";

// Same lists AddEditStockItem.js (the branch admin's own Stock screen)
// offers, so a group admin sees identical choices.
const CATEGORIES = [
  "Stationery",
  "Furniture",
  "Cleaning",
  "Sports",
  "Lab",
  "Electronics",
  "Other",
];
const UNITS = ["piece", "box", "packet", "set", "pair", "kg", "litre", "metre"];

/** Edit one catalogue entry in one branch. Quantity moves only via Adjust. */
export default function EditGroupStockModal({ groupId, item, open, onClose }) {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open || !item) return;
    form.setFieldsValue({
      name: item.name,
      category: item.category,
      unit: item.unit,
      low_stock_threshold: item.low_stock_threshold,
      description: item.description,
    });
  }, [open, item, form]);

  const updateMutation = useMutation(
    (values) => updateGroupStock(groupId, item.school, item._id, values),
    {
      onSuccess: () => {
        message.success("Stock item updated");
        queryClient.invalidateQueries("group-console-stock");
        onClose();
      },
      onError: (err) => {
        message.error(
          err?.response?.data?.error?.errors?.[0] || "Something went wrong",
        );
      },
    },
  );

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }
    updateMutation.mutate(values);
  };

  return (
    <Modal
      title={`Edit ${item?.name || "Stock Item"}`}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Save"
      confirmLoading={updateMutation.isLoading}
      destroyOnClose
    >
      <Form form={form} layout="vertical" className="pt-4">
        <Form.Item
          name="name"
          label="Item name"
          rules={[{ required: true, message: "Enter an item name" }]}
        >
          <Input placeholder="e.g. Chalk (box)" />
        </Form.Item>

        <div className="flex gap-4">
          <Form.Item name="category" label="Category" className="flex-1">
            <Select
              allowClear
              placeholder="Select a category"
              options={CATEGORIES.map((c) => ({ label: c, value: c }))}
            />
          </Form.Item>

          <Form.Item
            name="unit"
            label="Unit"
            className="flex-1"
            rules={[{ required: true, message: "Pick a unit" }]}
          >
            <Select
              showSearch
              options={UNITS.map((u) => ({ label: u, value: u }))}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="low_stock_threshold"
          label="Low stock alert at"
          tooltip="The item is flagged as low stock once quantity falls to or below this."
        >
          <InputNumber className="w-full" min={0} precision={0} />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Optional notes about this item" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
